import { msg } from "@lingui/core/macro";
import { type Component, For, Show } from "solid-js";
import {
  BarChart3,
  CheckCircle2,
  ListTodo,
  type LucideProps,
  ShieldOff,
  TrendingUp,
  UserCheck,
  Users,
  Wifi,
} from "@fleet/icons";
import { Card, CardContent, Skeleton } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { ClientTelemetryPanel } from "@/features/admin/components/client-telemetry-panel";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminMetrics = components["schemas"]["AdminMetrics"];

interface Stat {
  label: string;
  value: number;
  icon: Component<LucideProps>;
}

export function MetricsDashboard() {
  const { t } = useI18n();

  const query = createApiQuery<AdminMetrics, ErrorModel>(
    () => adminQueryKeys.metrics(),
    async () => api.GET("/api/admin/metrics", {}),
    () => ({ staleTime: 30_000 }),
  );

  const stats = (): Stat[] => {
    const m = query.data;
    if (!m) {
      return [];
    }
    return [
      { label: t(msg`Total users`), value: m.totalUsers, icon: Users },
      { label: t(msg`Verified users`), value: m.verifiedUsers, icon: UserCheck },
      { label: t(msg`Banned users`), value: m.bannedUsers, icon: ShieldOff },
      { label: t(msg`Active sessions`), value: m.activeSessions, icon: Wifi },
      { label: t(msg`New signups (7d)`), value: m.newSignups7d, icon: TrendingUp },
      { label: t(msg`New signups (30d)`), value: m.newSignups30d, icon: TrendingUp },
      { label: t(msg`Total lists`), value: m.totalLists, icon: ListTodo },
      { label: t(msg`Total todos`), value: m.totalTodos, icon: CheckCircle2 },
    ];
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <BarChart3 class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Metrics`)}</h2>
          <p class="text-sm text-muted-foreground">{t(msg`At-a-glance system health.`)}</p>
        </div>
      </div>

      {/* P23 / CC-60 · I3: "no response" != "nothing here". The block is
          WRAPPED (not chained): on error `isLoading` is `false`, so a
          `when={!isLoading && !isError}` with skeleton `fallback` would leave the error
          box and the skeleton at the same time, forever. */}
      <Show when={query.isError}>
        <QueryErrorState testId="metrics-error" compact onRetry={() => void query.refetch()} />
      </Show>
      <Show when={!query.isError}>
        <Show
          when={!query.isLoading}
          fallback={
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <For each={Array.from({ length: 8 }, () => 0)}>
                {() => <Skeleton class="h-28 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <For each={stats()}>
              {(stat) => (
                <Card>
                  <CardContent class="p-5 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-muted-foreground">{stat.label}</span>
                      <stat.icon class="h-4 w-4 text-primary" />
                    </div>
                    <div class="text-3xl font-bold tracking-tight">
                      {stat.value.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>

          {/* Client version census
              (infra/CLIENT_TELEMETRY_PROPAGATION.md). Hangs off this screen and
              not a new one because /admin/metrics already exists, is already gated
              by `metrics.read` and is already looked at by whoever decides whether to bump
              `min_supported`. */}
          <Show when={query.data?.clientTelemetry}>
            {(telemetry) => <ClientTelemetryPanel telemetry={telemetry()} />}
          </Show>
        </Show>
      </Show>
    </div>
  );
}
